import { deleteCookie } from '@jwplayer/fe-common/module/cookies';
import { WuiBanner } from '@jwplayer/wui-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { PR_NUMBER_ALERT, usePageTargetedAlerts } from '/module/page-targeted-alerts';
import type { PageTargetedAlert as PageTargetedAlertType } from '/module/page-targeted-alerts/types';

import css from './style.module.scss';

function PrAlert() {
  const [dismissed, setDismissed] = useState(false);

  if (!import.meta.env.APP_PR_NUMBER || dismissed) {
    return null;
  }

  const prNumber = import.meta.env.APP_PR_NUMBER;

  const builtAtStr = import.meta.env.APP_BUILD_TIME
    ? ` built at ${new Date(import.meta.env.APP_BUILD_TIME).toLocaleString()}`
    : '';

  return (
    <PageTargetedAlert
      alert={PR_NUMBER_ALERT}
      onDismiss={() => {
        setDismissed(true);
      }}
    >
      You are viewing&nbsp;
      <a href={`https://gitlab.com/jwpconnatix/groups/coreexperiences/dashboard/-/merge_requests/${prNumber}`}>
        PR {prNumber}
      </a>
      {builtAtStr}. When you are done, you can&nbsp;
      <a
        href="/"
        onClick={(e) => {
          e.preventDefault();
          deleteCookie('PR_NUMBER', {
            Path: '/',
          });
          window.location.pathname = '/';
        }}
      >
        exit
      </a>
      .
    </PageTargetedAlert>
  );
}

function PageTargetedAlert({
  alert,
  onDismiss,
  children,
}: {
  alert: PageTargetedAlertType;
  onDismiss: (id: string) => void;
  children?: React.ReactNode;
}) {
  let type: React.ComponentProps<typeof WuiBanner>['type'];

  switch (alert.type) {
    case 'warning':
      type = 'warning';
      break;
    case 'promo':
      type = 'promo';
      break;
    default:
      type = 'info';
      break;
  }

  return (
    <motion.div
      style={{ overflow: 'hidden' }}
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      exit={{ height: 0 }}
      transition={{ duration: 0.45 }}
    >
      <WuiBanner
        type={type}
        heading={alert.title}
        promoLabel={alert.promo_label || 'New Feature'}
        onDismiss={(e) => {
          // Prevent WUI from removing the DOM element
          e.preventDefault();

          alert.alert_id && onDismiss(alert.alert_id);
        }}
        dismissible={alert.dismissible}
      >
        {alert.body}
        {children}
        {alert.link_url && alert.link_text && (
          <>
            &nbsp;
            <a href={alert.link_url} rel="noopener noreferrer" target="_blank">
              {alert.link_text}
            </a>
          </>
        )}
      </WuiBanner>
    </motion.div>
  );
}

export function PageTargetedAlerts() {
  const { alerts, dismiss } = usePageTargetedAlerts();

  return (
    <div className={css.pageTargetedAlerts}>
      {/*
        The presenceAffectsLayout={false} is a workaround fix to avoid preact debug from spamming us with
        the "you are calling useMemo/useCallback without passing arguments" warnings. Unfortunately
        former-motion has a call to useCallback with second argument undefined, and preact debug really
        doesn't like that. See following links:
        - https://github.com/framer/motion/blob/v3.7.0/src/components/AnimatePresence/PresenceChild.tsx#L59
        - https://github.com/preactjs/preact/blob/10.5.7/debug/src/debug.js#L339
      */}
      <AnimatePresence presenceAffectsLayout={false}>
        {alerts.map((alert) => {
          return <PageTargetedAlert key={alert.alert_id} alert={alert} onDismiss={dismiss} />;
        })}
        <PrAlert />
      </AnimatePresence>
    </div>
  );
}
